import {
  useId,
  useRef,
  useState,
  useEffect,
  Fragment,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import {
  useItems,
  useAlerts,
  useSettings,
  useAuthentication,
  useWishlists,
} from '@dinosave/core/stores';
import type {
  ImageEntity,
  ItemEntity,
  WishlistEntity,
} from '@dinosave/core/types';
import {
  imageToBase64,
} from '@dinosave/core/utils';
import type {
  CurrencyEntity,
} from '@dinosave/scraping/types';
import posthog from '@/analytics/posthog';
import ItemUrl from '@/components/Items/ItemUrl';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import SelectCurrencyModal from '@/components/Modals/SelectCurrencyModal';
import SelectWishlistModal from '@/components/Modals/SelectWishlistModal';
import {
  CURRENCIES,
} from '@/constants';
import {
  AUTH,
  USER,
  WISHLIST,
} from '@/constants/namespaces';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Select from '@/ui/components/Select';
import Text from '@/ui/components/Text';
import Toggle from '@/ui/components/Toggle';
import {
  UploadIcon,
  DotsIcon,
  ClipboardIcon,
  ShoppingBagIcon,
} from '@/ui/icons';

import styles from './AddItemModal.module.css';

const USD = CURRENCIES.find(c => c.code === 'USD');

type AddItemModalProps = ModalProps & {
  item?: ItemEntity;
  wishlist?: WishlistEntity;
  namespace?: string;
};

const AddItemModal = (props: AddItemModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen,
    item: defaultItem,
    namespace = AUTH,
    onClose,
    ...rest
  } = props;

  const defaultUrl = defaultItem?.url || '';
  const defaultName = defaultItem?.name || '';
  const defaultNotify = defaultItem?.notify || false;
  const defaultCurrentPrice = defaultItem?.currentPrice || '';
  const defaultWishlistId = props?.wishlist?.id || defaultItem?.wishlistId;
  const defaultImage = defaultItem?.image as ImageEntity;
  const defaultImageUrl = defaultImage?.medium?.url || '';

  const id = useId();

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const settings = useSettings(s => s.settings);
  const defaultCurrencyCode = defaultItem?.currency ||
                              settings?.currency ||
                              USD.code;
  const defaultCurrency = CURRENCIES.find(c => c.code === defaultCurrencyCode) || USD;

  const items = useItems(USER, s => s.items) || [];
  const wishlistItems = useItems(WISHLIST, s => s.items) || [];
  const itemsUserSlug = useItems(USER, s => s.itemsUserSlug);
  const itemsWishlistSlug = useItems(USER, s => s.itemsWishlistSlug);
  const wishlistItemsWishlistSlug = useItems(WISHLIST, s => s.itemsWishlistSlug);
  const setItems = useItems(USER, s => s.setItems);
  const setWishlistItems = useItems(WISHLIST, s => s.setItems);
  const createUserItem = useItems(namespace, s => s.createUserItem);
  const updateUserItem = useItems(namespace, s => s.updateUserItem);

  const wishlists = useWishlists(AUTH, s => s.wishlists) || [];
  const defaultWishlist = wishlists?.find(w => w.id === defaultWishlistId);

  const addSuccess = useAlerts(s => s.addSuccess);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const wishlistRef = useRef(null);
  const currencyRef = useRef(null);
  const wishlistPortal = wishlistRef.current;
  const currencyPortal = currencyRef.current;

  const [url, setURL] = useState(defaultUrl);
  const [urlIsInvalid, setURLInvalid] = useState(false);
  const [name, setName] = useState(defaultName);
  const [nameInvalid, setNameInvalid] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(defaultCurrentPrice);
  const [currency, setCurrency] = useState<CurrencyEntity>(defaultCurrency);
  const [image, setImage] = useState('');
  const [notify, setNotify] = useState(defaultNotify);
  const [wishlist, setWishlist] = useState<WishlistEntity>(defaultWishlist);
  const [item, setItem] = useState<ItemEntity>();
  const [loading, setLoading] = useState(false);

  const selectCurrencyModal = useModal();
  const selectWishlistModal = useModal();

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const wishlistId = wishlist?.id;
  const wishlistEmoji = wishlist?.emoji;
  const wishlistIcon = wishlistId ? ShoppingBagIcon : DotsIcon;
  const wishlistName = wishlist?.name || 'Everything';
  const wishlistItemsCount = wishlist?.itemsCount || 0;
  const {
    code,
    symbols,
  } = currency;
  const currencyName = `${symbols.length ? `${symbols[0]} ` : ''} ${code}`;
  const imageUrlOrBase64 = image || defaultImageUrl;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleWishlistModal = () => {
    if (
      loading
    ) return;

    selectWishlistModal.toggle();
  };

  const handleCurrencyModal = () => {
    if (
      loading
    ) return;

    selectCurrencyModal.toggle();
  };

  const handleNotifyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNotify(e.target.checked);
  };

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCurrentPrice(e.target.value);
  };

  const handleURLChange = (url: string) => {
    setURL(url);
    setURLInvalid(false);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameInvalid(false);
  };

  const handleWishlistChange = (wishlist: WishlistEntity) => {
    setWishlist(wishlist);
    selectWishlistModal.close();
  };

  const handleCurrencyChange = (currency: CurrencyEntity) => {
    setCurrency(currency);
    selectCurrencyModal.close();
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const image = await imageToBase64(e.target.files[0]);
    setImage(image);
  };

  const handleImagePaste = async () => {
    const clipboardItems = await navigator.clipboard.read();

    for (const clipboardItem of clipboardItems) {
      const imageType = clipboardItem.types.find(type => type.startsWith('image/'));

      if (
        !imageType
      ) continue;

      const blob = await clipboardItem.getType(imageType);
      const image = await imageToBase64(blob);
      setImage(image);
    }
  };

  const handleItemParse = (item: ItemEntity) => {
    setItem(item);

    const itemPrice = item.currentPrice.toString();
    const itemCurrency = CURRENCIES.find(c => c.code === item.currency);

    if (
      !name &&
      item.name
    ) setName(item.name);

    if (
      !image &&
      item.image
    ) setImage(item.image as string);

    if (
      itemCurrency
    ) setCurrency(itemCurrency);

    if (
      !currentPrice &&
      item.currentPrice
    ) setCurrentPrice(itemPrice);
  };

  const handleCreate = async (item: ItemEntity) => {
    const createdItem = await createUserItem(authenticatedUserSlug, item);

    posthog.capture(posthog.events.ITEM_SAVED, { url });

    addSuccess({
      image,
      title: 'Saved to',
      message: wishlistName,
    });

    const itemWishlist = createdItem.wishlist as WishlistEntity;

    const isSameWishlist = itemWishlist?.slug === wishlistItemsWishlistSlug;
    const isSameItemsUser = itemsUserSlug === authenticatedUserSlug;
    const isSameItemsWishlist = !itemsWishlistSlug ||
                                itemsWishlistSlug === itemWishlist?.slug;

    if (
      isSameWishlist
    ) setWishlistItems([createdItem, ...wishlistItems]);

    if (
      isSameItemsUser && isSameItemsWishlist
    ) setItems([createdItem, ...items]);
  };

  const handleUpdate = async (item: ItemEntity) => {
    const updatedItem = await updateUserItem(authenticatedUserSlug, item);

    addSuccess({
      image,
      title: 'Saved',
      message: updatedItem.name,
    });

    const itemWishlist = updatedItem.wishlist as WishlistEntity;

    const isSameWishlist = itemWishlist?.slug === wishlistItemsWishlistSlug;
    const isSameItemsUser = itemsUserSlug === authenticatedUserSlug;
    const isSameItemsWishlist = !itemsWishlistSlug ||
                                itemsWishlistSlug === itemWishlist?.slug;
  };

  const handleSubmit = async () => {
    if (
      !authenticatedUser
    ) return;

    if (
      !url
    ) setURLInvalid(true);

    if (
      !name
    ) setNameInvalid(true);

    if (
      !url ||
      !name
    ) return;

    try {
      setLoading(true);

      const itemData = {
        ...item,
        url,
        name,
        image,
        notify,
        currency: code,
        wishlistId,
        currentPrice: Number(currentPrice),
      } as ItemEntity;

      if (
        defaultItem
      ) {
        await handleUpdate(itemData);
      } else {
        await handleCreate(itemData);
      }

      onClose();
    } finally {
      setLoading(false);
    }
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      !isOpen
    ) return;

    setName(defaultName);
    setNameInvalid(false);
    setURL(defaultUrl);
    setURLInvalid(false);
    setCurrentPrice(defaultCurrentPrice);
    setCurrency(defaultCurrency);
    setNotify(defaultNotify);
    setImage(undefined);
    setItem(defaultItem);
    setWishlist(defaultWishlist);
    setLoading(false);
  }, [isOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <SelectCurrencyModal
        {...selectCurrencyModal}
        onChange={handleCurrencyChange}
        portal={currencyPortal}
        allowScrolling
        dropdown
      />

      <SelectWishlistModal
        {...selectWishlistModal}
        onChange={handleWishlistChange}
        portal={wishlistPortal}
        allowScrolling
        dropdown
      />

      <Modal
        {...rest}
        isOpen={isOpen}
        onClose={onClose}
        onCancel={onClose}
        onConfirm={handleSubmit}
        className={styles.addItemModal}
        contentClassName={styles.content}
      >
        <Text kind="label">
          LINK
        </Text>

        <ItemUrl
          invalid={urlIsInvalid}
          defaultValue={defaultUrl}
          onChange={handleURLChange}
          onSuccess={handleItemParse}
        />

        <div className={styles.item}>
          <div className={styles.image}>
            <label htmlFor={id}>
              {imageUrlOrBase64 ? (
                <img src={imageUrlOrBase64} />
              ) : (
                <Fragment>
                  <UploadIcon />
                  <Text
                    kind="caption"
                    size="large"
                    color="neutral-500"
                  >
                    Upload an image
                  </Text>
                </Fragment>
              )}
            </label>
            <input
              id={id}
              type="file"
              onChange={handleImageChange}
              disabled={loading}
            />

            <div className={styles.paste}>
              <Button
                color="secondary"
                roundness="round"
                onClick={handleImagePaste}
                centered
              >
                <ClipboardIcon color="var(--neutral-600)" />
              </Button>
            </div>
          </div>

          <div className={styles.details}>
            <div className={styles.name}>
              <Text kind="label">
                NAME
              </Text>
              <Input
                value={name}
                onChange={handleNameChange}
                placeholder="Crocs unisex classic model in black"
                disabled={loading}
                invalid={nameInvalid}
              />
            </div>

            <div className={styles.price}>
              <div className={styles.priceValue}>
                <Text kind="label">
                  PRICE
                </Text>
                <Input
                  type="number"
                  value={String(currentPrice)}
                  onChange={handlePriceChange}
                  placeholder="27.99"
                  disabled={loading}
                />
              </div>
              <div
                ref={currencyRef}
                className={styles.priceCurrency}
              >
                <Select
                  kind="ghost"
                  disabled={loading}
                  isActive={selectCurrencyModal.isOpen}
                  onClick={handleCurrencyModal}
                >
                  <Text
                    kind="control"
                    size="regular"
                  >
                    {currencyName}
                  </Text>
                </Select>
              </div>
            </div>

            <div
              ref={wishlistRef}
              className={styles.wishlist}
            >
              <Select
                icon={wishlistIcon}
                emoji={wishlistEmoji}
                isActive={selectWishlistModal.isOpen}
                onClick={handleWishlistModal}
                roundness="rounded"
              >
                <div className={styles.wishlistText}>
                  <Text
                    kind="control"
                    size="small"
                    weight="600"
                  >
                    {wishlistName}
                  </Text>
                  <Text
                    kind="control"
                    size="small"
                    color="neutral-500"
                  >
                    {wishlistItemsCount} items
                  </Text>
                </div>
              </Select>
            </div>

            <label
              htmlFor="notify"
              className={styles.notifications}
            >
              <div className={styles.notificationsText}>
                <Text
                  kind="caption"
                  size="large"
                  weight="600"
                >
                  Receive price updates
                </Text>

                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                  weight="500"
                >
                  Notify me when price drops by 5% or more
                </Text>
              </div>

              <Toggle
                id="notify"
                checked={notify}
                onChange={handleNotifyChange}
              />
            </label>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

export default AddItemModal;
